import { Mesh, type Group, type Object3D } from 'three'
import BaseFileLoader from './base-file-loader'
import { ThreeMFLoader } from 'three/examples/jsm/Addons.js'
import { ThreeItemKeys, ThreeOriginalFileTypes } from '../../../constants/three'
import { setFileLoadingStatus } from '../../../stores/file-loading'

class ThreeMFFileLoader extends BaseFileLoader {
  protected getWorkerFileType(): FileParserSupportedType {
    return '3mf'
  }

  async load(): Promise<Group> {
    return this.loadWithProgress(async () => {
      if (!this.arrayBuffer) {
        await this.loadArrayBuffer()
      }

      let children: Object3D[]

      if (this.shouldUseWorker()) {
        const parsedObjects = await this.loadWithWorker()
        children = this.processParsedObjects(parsedObjects)
      }
      else {
        children = this.processOnMainThread()
      }

      return this.addMeshesToScene(children, this.file, () => ({
        [ThreeItemKeys.ORIGINAL_FILE_TYPE]: ThreeOriginalFileTypes.THREEMF,
      }))
    })
  }

  private processParsedObjects(parsedObjects: Object3D[]): Object3D[] {
    const children: Object3D[] = []

    for (const obj of parsedObjects) {
      obj.traverse((child) => {
        const newChild = child.clone() as Mesh
        const newMaterial = this.getStandardMaterial()
        newChild.traverse((item) => {
          if ('material' in item) {
            item.material = newMaterial
          }
        })
        children.push(this.scaleDownObject(newChild))
      })
    }

    return children
  }

  private processOnMainThread(): Object3D[] {
    setFileLoadingStatus('parsing', this.file.name, 30)

    const loader = new ThreeMFLoader()
    const item = loader.parse(this.arrayBuffer!)

    this.arrayBuffer = undefined

    setFileLoadingStatus('processing', this.file.name, 50)

    const children = item.children.map((child) => {
      const newChild = child.clone() as Mesh
      const newMaterial = this.getStandardMaterial()
      newChild.traverse((item) => {
        if ('material' in item) {
          item.material = newMaterial
        }
      })
      return this.scaleDownObject(newChild)
    })

    setFileLoadingStatus('finalizing', this.file.name, 80)

    return children
  }
}

export default ThreeMFFileLoader
